import { trackPromise } from 'react-promise-tracker';
import api from '../../../../../utils/API';
import { CROSS_ELASTICITY_URL } from '../constants';
import type { FilterOption, LocationType, ProductFilterOption } from '../types';

export type SearchProductsParams = {
  query?: string;
  page?: number;
  size?: number;
};

type ElasticityProductResponse = {
  gtin: string;
  descricao: string;
  secao?: string | null;
  categoria?: string | null;
  imagem?: string | null;
};

const mapProductResponse = (item: ElasticityProductResponse): ProductFilterOption => ({
  label: `${item.gtin} - ${item.descricao || 'Sem descrição'}`,
  value: item.gtin,
  imagem: item.imagem ?? null,
});

/** Tela 0 — catálogo válido (Painel Geral + STP), não apenas seleções já salvas. */
export const searchProducts = async ({ query, size = 50 }: SearchProductsParams): Promise<ProductFilterOption[]> => {
  const term = query?.trim() ?? '';

  return trackPromise(
    api({
      url: `${CROSS_ELASTICITY_URL}/products`,
      method: 'POST',
      data: term ? { query: term } : {},
      params: { size },
      expectToken: true,
    }).then(resp => {
      const items = (resp.data || []) as ElasticityProductResponse[];
      return items.map(mapProductResponse);
    }),
    'elasticidade-products',
  );
};

export type SegmentResponse = {
  segmento: { label: string; value: string };
  subsegmentos: { label: string; value: string }[];
};

export type GetSegmentsParams = {
  barcode: string;
  barcodeCompetitor?: string;
  segmentation?: string;
  subSegmentation?: string;
};

export type SearchLocationsParams = {
  type: LocationType;
  query: string;
  barcode?: string;
  barcodeCompetitor?: string;
  segmentation?: string;
  subSegmentation?: string;
};

export const getSegments = (params: GetSegmentsParams): Promise<SegmentResponse[]> => {
  return trackPromise(
    api({
      url: `${CROSS_ELASTICITY_URL}/segments`,
      method: 'POST',
      data: {
        barcode: params.barcode,
        ...(params.barcodeCompetitor && { barcodeCompetitor: params.barcodeCompetitor }),
        segmentation: params.segmentation || '',
        subSegmentation: params.subSegmentation || '',
      },
      expectToken: true,
    }).then(resp => resp.data as SegmentResponse[]),
    'elasticidade-segments',
  );
};

export const searchLocations = async ({
  type,
  query,
  barcode,
  barcodeCompetitor,
  segmentation,
  subSegmentation,
}: SearchLocationsParams): Promise<FilterOption[]> => {
  if (type === 'REGIAO') {
    if (!barcode) {
      return [];
    }

    return trackPromise(
      api({
        url: `${CROSS_ELASTICITY_URL}/regions`,
        method: 'POST',
        data: {
          barcode,
          ...(barcodeCompetitor && {
            barcodeCompetitor,
            // Contrato Python (ValidRegionsQuery) / Feign: garante filtro por canal no MNL
            barcode_competitor: barcodeCompetitor,
          }),
          ...(segmentation && { segmentation }),
          ...(subSegmentation && {
            subSegmentation,
            sub_segmentation: subSegmentation,
          }),
        },
        expectToken: true,
      })
        .then(resp => {
          const regions = (resp.data || []) as string[];
          const mapped = regions.map(r => ({
            label: r.charAt(0).toUpperCase() + r.slice(1).toLowerCase(),
            value: r,
          }));
          if (!query) return mapped;
          return mapped.filter(r => r.label.toLowerCase().includes(query.toLowerCase()));
        })
        .catch(() => []),
      'elasticidade-regions',
    );
  }
  return [];
};
